// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
// Constant imports
import { URLConstants } from '../../_constants/url-constants';
// model imports
import { CompletedConveyanceResultDto } from '../_models/completed-property-conveyance-result-dto';
import { OpenPropertyConveyanceResultDto } from '../_models/open-property-conveyance-result-dto';
import { ConveyanceCommunitySelectorResultDto } from '../_models/conveyance-community-selector-result-dto';
import { SearchConveyencePropertyResultDto } from '../_models/search-conveyance-property-result-dto';
import { ConveyedPropertyValidateResultDto } from '../_models/conveyed-property-validate-result-dto';
import { ConveyanceWelcomeEditorResultDto } from '../_models/conveyance-welcome-editor-result-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { BuyerOwnerEditorResultDto } from '../_models/buyer-owner-editor-result-dto';
import { BuyerOwnerResultDto } from '../_models/buyer-owner-result-dto';
import { BuyerOwnerValidationResultDto } from '../_models/buyer-owner-validation-result-dto';
import { OwnerPropertyResultDto } from '../_models/owner-property-result-dto';
import { ConveyanceAssessmentEditorResultDto } from '../_models/conveyance-assessment-editor-result-dto';
import { ConveyanceBillingRuleAssignmentResultDto } from '../_models/conveyance-billing-rule-assignment-result-dto';
import { ConveyanceClosingFeeEditorResultDto } from '../_models/conveyance-closing-fee-editor-result-dto';
import { ConveyanceEditorResultDto } from '../_models/conveyance-editor-result-dto';
import { ConveyanceCommunitySoaRequestResultDto } from '../_models/conveyance-community-soa-request-result-dto';
import { ConveyanceCommunitySoaFulfillmentResultDto } from '../_models/conveyance-community-soa-fulfillment-result-dto';
import { ConveyanceSoaAttachmentResultDto } from '../_models/conveyance-soa-attachment-result-dto';
import { ConveyanceCommunityFeeResultDto } from '../_models/conveyance-community-fee-result-dto';
import { CommunitySelectorResultDto } from '../../_models/community-selector-result-dto';
import { ConveyanceClosingDateValidationResultDto } from '../_models/conveyance-closing-date-validation-result-dto';
import { UnconveyancePropertyEditorResultDto } from '../_models/unconveyance-property-editor-result-dto';
import { UnconveyPropertyResultDto } from '../_models/unconvey-property-result-dto';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
export class PropertyConveyanceService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this._conveyanceCommunityChangeSubject = new Subject();
        this._conveyancePropertyChangeSubject = new Subject();
        this._buyerOwnerChangeSubject = new Subject();
        this._moveToNextStepSubject = new Subject();
    }
    onConveyanceCommunityChange() {
        return this._conveyanceCommunityChangeSubject;
    }
    changeConveyanceCommunity(value) {
        this._conveyanceCommunityChangeSubject.next(value);
    }
    onConveyancePropertyChange() {
        return this._conveyancePropertyChangeSubject;
    }
    changeConveyanceProperty(value) {
        this._conveyancePropertyChangeSubject.next(value);
    }
    onBuyerOwnerChange() {
        return this._buyerOwnerChangeSubject;
    }
    changeBuyerOwner(value) {
        this._buyerOwnerChangeSubject.next(value);
    }
    onMoveToNextStepChange() {
        return this._moveToNextStepSubject;
    }
    moveToNextStep() {
        this._moveToNextStepSubject.next();
    }
    getOpenWorkQueue() {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/open`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(OpenPropertyConveyanceResultDto, response)));
    }
    getCompletedWorkQueue(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/completed${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(CompletedConveyanceResultDto, response)));
    }
    getNoteDetails(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/notes${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(BaseProcessNotesResultDto, response)));
    }
    getCommunityEditorDetails() {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/editor`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(CommunitySelectorResultDto, response)));
    }
    getProcessCommunities(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/communities${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(ConveyanceCommunitySelectorResultDto, response)));
    }
    getSearchProperties(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/search/properties`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(SearchConveyencePropertyResultDto, response)));
    }
    validateProperty(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/property/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyedPropertyValidateResultDto, response)));
    }
    welcomeEditorDetails(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/property/welcome/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceWelcomeEditorResultDto, response)));
    }
    welcomeEditorValidate(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/property/welcome/editor/validate`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }
    buyerOwnerEditor(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(BuyerOwnerEditorResultDto, response)));
    }
    buyerOwnerCommunities(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/communities`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceCommunitySelectorResultDto, response)));
    }
    searchBuyerOwner(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/search/buyer/owners`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(BuyerOwnerResultDto, response)));
    }
    buyerOwnerValidate(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(BuyerOwnerValidationResultDto, response)));
    }
    buyerOwnerProperties(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/properties`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(OwnerPropertyResultDto, response)));
    }
    assessmentRuleEditor(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/assessment/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceAssessmentEditorResultDto, response)));
    }
    assessmentRuleValidate(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/assessment/validate`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }
    sellerCurrentBillingRule(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/billingruleassignments`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceBillingRuleAssignmentResultDto, response)));
    }
    saveNote(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/notes`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }
    saveCommunity(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community`;
        return this.httpClient.put(url, classToPlain(baseParams));
    }
    clearCommunity(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/clear`;
        return this.httpClient.put(url, classToPlain(baseParams));
    }
    updateWorkQueueStatus(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/status/collection`;
        return this.httpClient.put(url, classToPlain(baseParams));
    }
    deleteConveyance(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(baseParams)
        };
        return this.httpClient.delete(url, options);
    }
    getClosingFeeEditor(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/closingfee/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceClosingFeeEditorResultDto, response)));
    }
    closingFeeRuleValidate(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/closingfee/validate`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }
    getConveyanceEditor(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceEditorResultDto, response)));
    }
    getConveyanceSoaRequests(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soarequests`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceCommunitySoaRequestResultDto, response)));
    }
    getConveyanceSoaFulfillments(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soafulfillments`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceCommunitySoaFulfillmentResultDto, response)));
    }
    getSoaRequestAttachment(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soarequest/attachment`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceSoaAttachmentResultDto, response)));
    }
    downloadSoaRequestAttachment(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soarequest/attachment/download${baseParams.getQueryString}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((response) => new Blob([response])));
    }
    getConveyanceCommunityFees(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/fees`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceCommunityFeeResultDto, response)));
    }
    closingDateValidate(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/closingdate/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response) => plainToClass(ConveyanceClosingDateValidationResultDto, response)));
    }
    processConveyance(baseParams) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/process`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }
    getPaperWorkFile(fileUrl) {
        return this.httpClient
            .get(fileUrl, { responseType: 'arraybuffer' })
            .pipe(map((response) => new Blob([response])));
    }
    getUnconveyPropertyEditor(conveyedProperty) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/unconvey/editor${conveyedProperty.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(UnconveyancePropertyEditorResultDto, response)));
    }
    unconveyProperty(conveyedProperty) {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/unconvey`;
        return this.httpClient
            .put(url, classToPlain(conveyedProperty))
            .pipe(map((response) => plainToClass(UnconveyPropertyResultDto, response)));
    }
}
PropertyConveyanceService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function PropertyConveyanceService_Factory() { return new PropertyConveyanceService(i0.ɵɵinject(i1.HttpClient)); }, token: PropertyConveyanceService, providedIn: "root" });
